import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { App, Button, Empty } from "antd";
import { Device, Call } from "@twilio/voice-sdk";
import theme from "../../themes";
import { pageShellCss, pageTitleCss } from "../../styles/responsive";
import ToastMessage from "../../utils/toastMessage";
import { getAgentsThunk } from "../../store/actions/agent.action";
import { useAppDispatch } from "../../store";
import { IAgent } from "../../models/agent.model";
import {
  getLiveConferences,
  getMonitorToken,
  ILiveConference,
} from "../../services/conference.service";

const sipLabel = (address?: string) => {
  const text = String(address || "").replace(/^sip:/i, "").trim();
  return text || "";
};

const startedLabel = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const errorText = (error: unknown, fallback: string) => {
  if (typeof error === "string" && error.trim()) return error;
  if (error && typeof error === "object" && "response" in error) {
    const message = (error as { response?: { data?: { message?: string } } }).response
      ?.data?.message;
    if (message) return message;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

const ConferencePage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { message } = App.useApp();
  const toast = ToastMessage(message);
  const [conferences, setConferences] = useState<ILiveConference[]>([]);
  const [agents, setAgents] = useState<IAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState<string | null>(null);
  const [listening, setListening] = useState<string | null>(null);
  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const stopListening = useCallback(() => {
    callRef.current?.disconnect();
    callRef.current = null;
    deviceRef.current?.destroy();
    deviceRef.current = null;
    setListening(null);
    setJoining(null);
  }, []);

  const loadConferences = useCallback(async () => {
    const response = await getLiveConferences();
    const rows = response.data?.data?.conferences || [];
    setConferences(rows);
    return rows;
  }, []);

  const stopRef = useRef(stopListening);
  stopRef.current = stopListening;
  const reportedLoadError = useRef(false);

  useEffect(() => {
    let active = true;
    dispatch(
      getAgentsThunk({
        limit: 999,
        page: 1,
        keyword: "",
      })
    )
      .unwrap()
      .then((res) => {
        if (active) setAgents(res.data.agents || []);
      })
      .catch(() => undefined);

    const refresh = () => {
      loadConferences()
        .then(() => {
          reportedLoadError.current = false;
        })
        .catch((error) => {
          if (!active || reportedLoadError.current) return;
          reportedLoadError.current = true;
          toastRef.current.error(errorText(error, t("conference.loadFailed")));
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    };
    refresh();
    const timer = window.setInterval(refresh, 5000);
    return () => {
      active = false;
      window.clearInterval(timer);
      stopRef.current();
    };
  }, [dispatch, loadConferences, t]);

  useEffect(() => {
    if (!listening) return;
    if (!conferences.some((row) => row.conference === listening)) {
      stopRef.current();
      toastRef.current.warning(t("conference.callEnded"), 4);
    }
  }, [conferences, listening, t]);

  const groups = useMemo(() => {
    const byAgent = new Map<string, ILiveConference[]>();
    conferences.forEach((row) => {
      const key = row.agent_id || "";
      const current = byAgent.get(key) || [];
      current.push(row);
      byAgent.set(key, current);
    });
    return Array.from(byAgent.entries()).map(([agentId, calls]) => ({
      agentId,
      name:
        agents.find((agent) => agent._id === agentId)?.agent_name ||
        (agentId ? agentId : t("conference.unassigned")),
      calls,
    }));
  }, [agents, conferences, t]);

  const monitor = async (conference: string) => {
    if (joining) return;
    setJoining(conference);
    try {
      if (listening && listening !== conference) stopListening();
      const response = await getMonitorToken(conference);
      const token = response.data?.data?.token;
      if (!token) throw new Error(t("conference.joinFailed"));
      const device = new Device(token, { logLevel: "error" });
      deviceRef.current = device;
      // Listen-only: keep the mic off in the SDK as well as TwiML muted=true,
      // and ask the browser for echo cancellation so speaker bleed is reduced
      // when Linphone and the dashboard share one machine.
      const call = await device.connect({
        params: { conference },
        rtcConstraints: {
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        },
      });
      callRef.current = call;
      const enforceMute = () => {
        try {
          call.mute(true);
        } catch {
          /* ignore */
        }
      };
      call.on("accept", enforceMute);
      call.on("ringing", enforceMute);
      enforceMute();
      call.on("disconnect", () => {
        setListening((current) => (current === conference ? null : current));
        device.destroy();
        if (deviceRef.current === device) deviceRef.current = null;
        callRef.current = null;
      });
      setListening(conference);
    } catch (error) {
      stopListening();
      toast.error(errorText(error, t("conference.joinFailed")));
    } finally {
      setJoining(null);
    }
  };

  return (
    <Page>
      <Header>
        <Title>{t("conference.title")}</Title>
        <Hint>{t("conference.hint")}</Hint>
      </Header>
      {loading && conferences.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("conference.loading")} />
      ) : groups.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("conference.empty")} />
      ) : (
        groups.map((group) => (
          <Section key={group.agentId || "unassigned"}>
            <AgentName>{group.name}</AgentName>
            {group.calls.map((call) => {
              const dialed = sipLabel(call.guest_to) || sipLabel(call.guest_from) || t("conference.guest");
              const isListening = listening === call.conference;
              return (
                <CallRow key={call.conference}>
                  <CallInfo>
                    <CallTitle>{dialed}</CallTitle>
                    <CallMeta>
                      {sipLabel(call.guest_from) ? `${t("conference.from")} ${sipLabel(call.guest_from)}` : t("conference.guest")}
                      {call.started_at ? ` · ${startedLabel(call.started_at)}` : ""}
                    </CallMeta>
                  </CallInfo>
                  {isListening ? (
                    <Button danger onClick={stopListening}>
                      {t("conference.hangUp")}
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      loading={joining === call.conference}
                      disabled={Boolean(joining)}
                      onClick={() => monitor(call.conference)}
                    >
                      {t("conference.monitor")}
                    </Button>
                  )}
                </CallRow>
              );
            })}
          </Section>
        ))
      )}
    </Page>
  );
};

const Page = styled.div`
  background: ${theme.colors.grey3};
  display: flex;
  flex-direction: column;
  gap: 20px;
  ${pageShellCss}
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h1`
  margin: 0;
  font-weight: 600;
  font-size: 24px;
  line-height: 1;
  color: #000;
  ${pageTitleCss}
`;

const Hint = styled.p`
  margin: 0;
  max-width: 720px;
  color: ${theme.colors.grey1};
  line-height: 1.5;
`;

const Section = styled.section`
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AgentName = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`;

const CallRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0;
  border-top: 1px solid ${theme.colors.grey2};

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const CallInfo = styled.div`
  min-width: 0;
`;

const CallTitle = styled.div`
  font-weight: 600;
`;

const CallMeta = styled.div`
  color: ${theme.colors.grey1};
  font-size: 14px;
`;

export default ConferencePage;
